/**
 * The UM's final marking screen — `/um/umpires/:tmsPersonId/marking`.
 *
 * Authority: `um-app/06-report-studio.md` §4.3b (*"Bars, not boxes … the system computes the
 * final mark … one marking per umpire for the whole tournament … agreed with the Technical
 * Delegate"*) and `05-reporting-marking.md` §B3a/§B3b.
 *
 * **The bars are the Control Centre's own component.** `MarkingPanel` is mounted here
 * unchanged, with the UM's writers injected: §B3b's whole point is that *"the agreed set is
 * one record; there is no TD copy and UM copy to reconcile"*, and two components would be two
 * implementations of the outcome arithmetic waiting to disagree about somebody's career. What
 * differs between the surfaces is stated as props, not as a fork — the manager may push (write
 * path W3, §A5), and the phone layout is CSS under `.umapp`.
 *
 * The panel the API serves this screen carries **no date of birth**; the §B2 strip renders its
 * honest `—` for it, as it does for any official the roster has none for.
 */

import { Link, useParams } from "react-router-dom";

import { fetchMyUmpires, fetchUmMarkingPanel, putUmMark, pushUmMarks } from "../../api/client.js";
import { HowToLink, HowToUse } from "../../app/HowToUse.js";
import { FailBanner, Skeleton } from "../../app/states.js";
import { useAsync } from "../../app/useAsync.js";
import { UM_ROOT_PATH } from "../../app/UmShell.js";
import { MarkingPanel } from "../../features/marking/MarkingPanel.js";

/** §B3a's kicker for the final reporting screen, in the UM app's own wording (§4.3b). */
export const UM_MARKING_KICKER = "Final Marking · Whole Tournament";

export function UmMarkingPage(): React.JSX.Element {
  const { tmsPersonId } = useParams<{ tmsPersonId: string }>();
  const id = tmsPersonId ?? "";

  const panel = useAsync(() => fetchUmMarkingPanel(id), [id]);
  /**
   * The umpire's NAME, which the §B2 strip prints. The marking panel is keyed on a person id
   * and deliberately carries no name of its own, so it is read from the owner's own list —
   * the one projection on this surface that has one. The marking never waits on it: the strip
   * falls back to the id rather than holding the bars back for a display string.
   */
  const mine = useAsync(() => fetchMyUmpires(), []);
  const name =
    (mine.status === "ready"
      ? mine.data.umpires.find((umpire) => umpire.tmsPersonId === id)?.mediaName
      : undefined) ?? `TMS person ${id}`;

  return (
    <>
      <header className="umsect">
        {/* Back arrows name their destination, never a bare `‹` (gap J7). */}
        <Link className="umback" to={`${UM_ROOT_PATH}/umpires`}>
          ‹ Your Umpires
        </Link>
        <h2>Marking</h2>
        <span className="umsect__kicker">{UM_MARKING_KICKER}</span>
        <HowToUse id="um-marking" title="How to use Marking">
          <li>
            Drag each bar in the{" "}
            <HowToLink to="marking-panel">Marking Panel</HowToLink> to the mark you want. It
            saves as you go, so there is no Save button.
          </li>
          <li>
            Mark a criterion <b>N/A</b> where it did not apply. Its weight drops out of the
            total rather than scoring zero.
          </li>
          <li>
            These marks are given once, at the end of the tournament, and agreed with the
            Technical Delegate. He sees these bar positions from his own screen.
          </li>
          <li>
            When they are agreed, open <b>Report</b> for this umpire to write the words that go
            with them.
          </li>
        </HowToUse>
      </header>

      <p className="umnote">
        Given once, at the end · <b>Agreed With The Technical Delegate</b>. He sees these bar
        positions live from his own umpire row; the agreed set is what you push.
      </p>

      {panel.status === "loading" ? <Skeleton rows={10} /> : null}
      {panel.status === "error" ? <FailBanner error={panel.error} /> : null}
      {panel.status === "ready" ? (
        <MarkingPanel
          key={id}
          officialName={name}
          initialPanel={panel.data}
          write={putUmMark}
          push={pushUmMarks}
          // Write path W3: on her own umpire, the manager is the presser (§A5).
          pushable
        />
      ) : null}

      <p className="umfoot">
        <Link to={`${UM_ROOT_PATH}/umpires/${id}/report`}>Write the report</Link> — the report
        is its own press, and the Technical Delegate&rsquo;s closing remarks head it.
      </p>
    </>
  );
}
